import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useT } from "@/i18n/LanguageContext";
import { printStopLabel } from "@/lib/printStopLabel";
import { stopColorLabel } from "@/components/CancelStockStopDialog";

const FLAG = { china: "🇨🇳", usa: "🇺🇸", canada: "🇨🇦" };
const WH_LABEL = { china: "CHINA WAREHOUSE", usa: "AMERICA WAREHOUSE", canada: "CANADIAN WAREHOUSE" };
const STATION_ROLE = { china: "packing_orders", usa: "inventory_packing", canada: "inventory_packing" };
export const canStockIn = (user, dest) => ["back_office", "super_admin"].includes(user?.role) || user?.role === STATION_ROLE[dest];

/** Client-side mirror of the backend `_stock_in_due` for list rows (lists carry the raw order). */
export const stockInDue = (o) => {
  if (!o?.is_stock_unit || ["in_stock", "stock_used", "cancelled"].includes(o.status)) return false;
  const dest = o.stock_unit?.dest || "china";
  if (dest === "china") return !!o.packed_at && o.status === "packing";
  return !!o.received_at && o.status === "completed";
};

const STATE_CLS = { in_stock: "bg-emerald-600", reserved: "bg-sky-600", used: "bg-slate-500", missing: "bg-red-600", cancelled: "bg-slate-400" };

/** Small pill for list rows: 📦 STOCK → 🇨🇦 · state. */
export function StockChip({ o, testid }) {
  const { t } = useT();
  if (!o?.is_stock_unit) return null;
  const dest = o.stock_unit?.dest || "china";
  const due = stockInDue(o);
  const state = o.status === "in_stock" ? (o.stock_unit?.reserved_for ? "reserved" : "in_stock") : o.status === "stock_used" ? (o.stock_unit?.state || "used") : null;
  const cls = due ? "bg-red-600" : STATE_CLS[state] || "bg-indigo-600";
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-black uppercase tracking-wider text-white ${cls}`}
          data-testid={testid || `stock-chip-${o.id}`} title={`${t("Stock unit")} · ${t("batch")} #${o.stock_unit?.batch_no || "—"} → ${WH_LABEL[dest]}`}>
      📦 {t("STOCK")} → {FLAG[dest]}{due ? ` · ${t("STOCK-IN")}` : state ? ` · ${t(state.replace("_", " "))}` : ""}
    </span>
  );
}

/** Full-screen STOCK-IN screen: the set is on the destination shelf → print the storage label, confirm (+1 stock). */
export function StockInDialog({ orderId, open, onOpenChange, onDone }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { t, lang } = useT();
  const [bin, setBin] = useState("");
  const { data: o, isLoading } = useQuery({
    queryKey: ["order", orderId],
    queryFn: async () => (await api.get(`/orders/${orderId}`)).data,
    enabled: !!orderId && !!open,
  });
  const confirm = useMutation({
    mutationFn: () => api.post(`/orders/${orderId}/stock-in`, { bin: bin.trim() || null }),
    onSuccess: (r) => {
      qc.invalidateQueries();
      toast.success(`📦 ${o?.po_number} ${t("in stock")} · ${t(r?.data?.warehouse_label || "")}${r?.data?.bin ? ` · ${t("bin")} ${r.data.bin}` : ""}`);
      onOpenChange?.(false);
      onDone?.(r?.data);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not confirm"),
  });
  if (!open) return null;
  const info = o?.stock_unit_info;
  const dest = info?.dest || "china";
  const wrongStation = !!info && !canStockIn(user, dest);
  const colorLabel = info ? stopColorLabel(info.color, lang) : "—";
  const inStock = o?.status === "in_stock";
  const printLabel = () => {
    if (printStopLabel({ po: o.po_number, cat: info.cat_number, color: info.color, warehouse: dest, lang, stock: true })) toast.success(t("Label sent to the printer window"));
    else toast.error(t("Popup blocked — allow popups for this site, then print again"));
  };
  return createPortal(
    <div className="fixed inset-0 z-[200] bg-indigo-800 text-white overflow-y-auto" data-testid="stock-in" role="alertdialog" aria-modal="true">
      <div className="min-h-full flex flex-col items-center justify-center px-6 py-10 text-center">
        {isLoading || !o ? (
          <div className="text-2xl font-black">{t("Loading…")}</div>
        ) : !info ? (
          <>
            <div className="text-3xl font-black">{o.po_number} {t("is not a stock unit")}</div>
            <Button className="mt-6 bg-white text-indigo-800 hover:bg-indigo-100" onClick={() => onOpenChange?.(false)} data-testid="stock-in-close">{t("Close")}</Button>
          </>
        ) : (
          <>
            <div className="text-[96px] leading-none" aria-hidden>📦</div>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight uppercase" data-testid="stock-in-title">{t("STOCK-IN")} · {FLAG[dest]} {t(WH_LABEL[dest])}</h1>
            <p className="mt-3 text-xl sm:text-2xl font-bold uppercase tracking-wide max-w-3xl" data-testid="stock-in-headline">
              {o.po_number} — {t("not for a customer. Put it on the")} {t(WH_LABEL[dest])} {t("shelf, label it, then confirm.")}
            </p>
            <p className="mt-2 text-base sm:text-lg text-indigo-100 max-w-2xl" data-testid="stock-in-batch">
              {t("Batch order")} #{info.batch_no} · {t("ordered by")} {o.stock_unit?.created_by || "—"} · {[o.brand, o.model].filter(Boolean).join(" ")}
            </p>
            {!info.due && !inStock && (
              <div className="mt-4 rounded-xl bg-yellow-300 text-yellow-950 px-5 py-3 max-w-2xl font-semibold" data-testid="stock-in-not-due">
                {dest === "china" ? t("Finish packing first — the STOCK-IN appears then.") : t("Mark the set received first — the STOCK-IN appears then.")}
              </div>
            )}

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 w-full max-w-3xl">
              <div className="rounded-2xl bg-white text-slate-900 p-6">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-mono">{t("Catalog number")}</div>
                <div className="mt-1 text-3xl sm:text-4xl font-black font-mono break-words" data-testid="stock-in-cat">{info.cat_number || "— MISSING —"}</div>
              </div>
              <div className="rounded-2xl bg-white text-slate-900 p-6">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-mono">{t("Colour")}</div>
                <div className="mt-1 text-3xl sm:text-4xl font-black" data-testid="stock-in-color">{colorLabel}</div>
              </div>
              <div className="rounded-2xl bg-white text-slate-900 p-6">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-mono">{t("Unit number")}</div>
                <div className="mt-1 text-2xl sm:text-3xl font-black font-mono break-all" data-testid="stock-in-unit">{o.po_number}</div>
              </div>
            </div>

            {!wrongStation && (
              <div className="mt-6 flex flex-wrap gap-3 justify-center items-center">
                <Button className="h-auto min-h-12 py-2 px-6 text-base font-black bg-white text-slate-900 hover:bg-slate-100 whitespace-normal max-w-full" onClick={printLabel} data-testid="stock-in-print-label">
                  🖨 {t("Print storage label")} · {info.cat_number || "—"} · {colorLabel}
                </Button>
                {!inStock && (
                  <label className="inline-flex items-center gap-2 text-sm font-semibold">
                    {t("Bin / shelf")}
                    <Input value={bin} onChange={(e) => setBin(e.target.value)} placeholder={info.bin || o.bin_number || "A-12"} className="h-12 w-32 text-slate-900 font-mono text-lg" data-testid="stock-in-bin" />
                  </label>
                )}
              </div>
            )}

            {wrongStation ? (
              <div className="mt-8 rounded-xl bg-yellow-300 text-yellow-950 px-5 py-4 max-w-2xl font-semibold" data-testid="stock-in-wrong-station">
                {t("This set goes to the")} {FLAG[dest]} {t(WH_LABEL[dest])} — <b>{t(info.station_label)}</b> {t("stocks it, not your station.")}
              </div>
            ) : inStock ? (
              <div className="mt-8 rounded-xl bg-emerald-500 text-white px-5 py-4 max-w-2xl font-semibold" data-testid="stock-in-done">
                ✓ {t("In stock")} · {t(WH_LABEL[dest])} · {t("by")} {info.in_stock_by}{info.in_stock_at ? ` · ${new Date(info.in_stock_at).toLocaleString()}` : ""}{info.bin ? ` · ${t("bin")} ${info.bin}` : ""}
                {info.reserved_for && <div className="mt-1 text-sm">{t("Reserved for")} <b>{info.reserved_for.po_number}</b></div>}
              </div>
            ) : null}

            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              {!inStock && (
                <Button
                  className="h-auto min-h-14 py-3 px-8 text-lg font-black bg-white text-indigo-800 hover:bg-indigo-100 disabled:opacity-40 whitespace-normal max-w-full"
                  disabled={wrongStation || (!info.due && !["back_office", "super_admin"].includes(user?.role)) || confirm.isPending}
                  onClick={() => confirm.mutate()}
                  data-testid="stock-in-confirm"
                >
                  {confirm.isPending ? t("Saving…") : `✓ ${t("On the")} ${t(WH_LABEL[dest])} ${t("shelf — add to stock")}`}
                </Button>
              )}
              <Button variant="ghost" className="h-14 px-6 text-white hover:bg-indigo-900" onClick={() => onOpenChange?.(false)} data-testid="stock-in-later">
                {inStock ? t("Close") : t("Later")}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

/** Small button that opens the STOCK-IN screen (lists, order page). */
export function StockInButton({ orderId, due = true, size = "sm", className = "", onDone }) {
  const [open, setOpen] = useState(false);
  const { t } = useT();
  return (
    <>
      <Button
        size={size}
        className={`${due ? "bg-red-600 hover:bg-red-700" : "bg-indigo-600 hover:bg-indigo-700"} text-white font-black ${className}`}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        data-testid={`stock-in-open-${orderId}`}
        title={due ? t("Stock unit — put it on the shelf and confirm") : t("Stock unit — STOCK-IN appears after packing / receiving")}
      >
        📦 {due ? t("STOCK-IN") : t("STOCK")}
      </Button>
      <StockInDialog orderId={orderId} open={open} onOpenChange={setOpen} onDone={onDone} />
    </>
  );
}
